// Player inventory model: categories, catalog of common 5e items, currency,
// and v2 helpers for weight / encumbrance / currency math (added when the
// shop system landed).

export const INVENTORY_CATEGORIES = [
  { id: 'weapon',     label: 'Weapons',     icon: '⚔'  },
  { id: 'armor',      label: 'Armor',       icon: '🛡' },
  { id: 'consumable', label: 'Consumables', icon: '🧪' },
  { id: 'gear',       label: 'Gear',        icon: '🎒' },
  { id: 'treasure',   label: 'Treasure',    icon: '💎' },
];

// Categories where the equipped flag is meaningful (other categories suppress the toggle).
export const EQUIPPABLE_CATEGORIES = new Set(['weapon', 'armor']);

// 5e coin denominations, ordered platinum → copper for display.
export const CURRENCY_DENOMS = [
  { id: 'pp', label: 'PP', cpValue: 1000 },
  { id: 'gp', label: 'GP', cpValue: 100  },
  { id: 'ep', label: 'EP', cpValue: 50   },
  { id: 'sp', label: 'SP', cpValue: 10   },
  { id: 'cp', label: 'CP', cpValue: 1    },
];

export function totalInGp(currency) {
  if (!currency) return '0.00';
  let cp = 0;
  for (const d of CURRENCY_DENOMS) cp += (currency[d.id] || 0) * d.cpValue;
  return (cp / 100).toFixed(2);
}

export function totalInCp(currency) {
  if (!currency) return 0;
  let cp = 0;
  for (const d of CURRENCY_DENOMS) cp += (currency[d.id] || 0) * d.cpValue;
  return cp;
}

export function createItem(category, presetFields = {}) {
  return {
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category,
    name: '',
    qty: 1,
    description: '',
    equipped: false,
    weightLb: 0,
    ...presetFields,
  };
}

// Common 5e SRD items grouped per category. Players can pick from this and
// fall back to a custom-item form for homebrew / magic items.
//
// Per-category structured fields (used by Inventory v2 mechanical hooks):
//  - weapon: { tier, damageDie, damageType, properties[], stat, versatileDie? }
//  - armor:  { type, baseAc?, dexCap?, acBonus?, stealthDis? }
//  - use:    { type: 'heal' | 'buff' | null, dice?, bonus?, buffId?, duration? }
//  - weightLb: number — used by encumbrance helpers (PHB / SRD weights).
export const INVENTORY_CATALOG = [
  // ── Weapons (martial + simple, common picks) ───────────────────────────────
  { category: 'weapon', name: 'Dagger',         description: '1d4 piercing · finesse, light, thrown 20/60', weightLb: 1,
    weapon: { tier: 'simple', damageDie: 'd4', damageType: 'piercing', properties: ['finesse','light','thrown'], stat: 'str' } },
  { category: 'weapon', name: 'Handaxe',        description: '1d6 slashing · light, thrown 20/60', weightLb: 2,
    weapon: { tier: 'simple', damageDie: 'd6', damageType: 'slashing', properties: ['light','thrown'], stat: 'str' } },
  { category: 'weapon', name: 'Spear',          description: '1d6 piercing · thrown 20/60, versatile (1d8)', weightLb: 3,
    weapon: { tier: 'simple', damageDie: 'd6', damageType: 'piercing', properties: ['thrown','versatile'], versatileDie: 'd8', stat: 'str' } },
  { category: 'weapon', name: 'Shortsword',     description: '1d6 piercing · finesse, light', weightLb: 2,
    weapon: { tier: 'martial', damageDie: 'd6', damageType: 'piercing', properties: ['finesse','light'], stat: 'str' } },
  { category: 'weapon', name: 'Longsword',      description: '1d8 slashing · versatile (1d10)', weightLb: 3,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'slashing', properties: ['versatile'], versatileDie: 'd10', stat: 'str' } },
  { category: 'weapon', name: 'Greatsword',     description: '2d6 slashing · heavy, two-handed', weightLb: 6,
    weapon: { tier: 'martial', damageDie: '2d6', damageType: 'slashing', properties: ['heavy','two-handed'], stat: 'str' } },
  { category: 'weapon', name: 'Rapier',         description: '1d8 piercing · finesse', weightLb: 2,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'piercing', properties: ['finesse'], stat: 'str' } },
  { category: 'weapon', name: 'Scimitar',       description: '1d6 slashing · finesse, light', weightLb: 3,
    weapon: { tier: 'martial', damageDie: 'd6', damageType: 'slashing', properties: ['finesse','light'], stat: 'str' } },
  { category: 'weapon', name: 'Mace',           description: '1d6 bludgeoning', weightLb: 4,
    weapon: { tier: 'simple', damageDie: 'd6', damageType: 'bludgeoning', properties: [], stat: 'str' } },
  { category: 'weapon', name: 'Morningstar',    description: '1d8 piercing', weightLb: 4,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'piercing', properties: [], stat: 'str' } },
  { category: 'weapon', name: 'Warhammer',      description: '1d8 bludgeoning · versatile (1d10)', weightLb: 2,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'bludgeoning', properties: ['versatile'], versatileDie: 'd10', stat: 'str' } },
  { category: 'weapon', name: 'Battleaxe',      description: '1d8 slashing · versatile (1d10)', weightLb: 4,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'slashing', properties: ['versatile'], versatileDie: 'd10', stat: 'str' } },
  { category: 'weapon', name: 'Greataxe',       description: '1d12 slashing · heavy, two-handed', weightLb: 7,
    weapon: { tier: 'martial', damageDie: 'd12', damageType: 'slashing', properties: ['heavy','two-handed'], stat: 'str' } },
  { category: 'weapon', name: 'Quarterstaff',   description: '1d6 bludgeoning · versatile (1d8)', weightLb: 4,
    weapon: { tier: 'simple', damageDie: 'd6', damageType: 'bludgeoning', properties: ['versatile'], versatileDie: 'd8', stat: 'str' } },
  { category: 'weapon', name: 'Shortbow',       description: '1d6 piercing · range 80/320, two-handed', weightLb: 2,
    weapon: { tier: 'simple', damageDie: 'd6', damageType: 'piercing', properties: ['ranged','two-handed'], stat: 'dex' } },
  { category: 'weapon', name: 'Longbow',        description: '1d8 piercing · heavy, range 150/600, two-handed', weightLb: 2,
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'piercing', properties: ['ranged','heavy','two-handed'], stat: 'dex' } },
  { category: 'weapon', name: 'Light crossbow', description: '1d8 piercing · range 80/320, loading, two-handed', weightLb: 5,
    weapon: { tier: 'simple', damageDie: 'd8', damageType: 'piercing', properties: ['ranged','loading','two-handed'], stat: 'dex' } },
  { category: 'weapon', name: 'Heavy crossbow', description: '1d10 piercing · heavy, range 100/400, loading, two-handed', weightLb: 18,
    weapon: { tier: 'martial', damageDie: 'd10', damageType: 'piercing', properties: ['ranged','heavy','loading','two-handed'], stat: 'dex' } },

  // ── Armor + shields ─────────────────────────────────────────────────────────
  { category: 'armor', name: 'Padded',         description: 'Light · AC 11 + Dex · disadvantage on Stealth', weightLb: 8,
    armor: { type: 'light', baseAc: 11, stealthDis: true } },
  { category: 'armor', name: 'Leather',        description: 'Light · AC 11 + Dex', weightLb: 10,
    armor: { type: 'light', baseAc: 11 } },
  { category: 'armor', name: 'Studded leather',description: 'Light · AC 12 + Dex', weightLb: 13,
    armor: { type: 'light', baseAc: 12 } },
  { category: 'armor', name: 'Hide',           description: 'Medium · AC 12 + Dex (max 2)', weightLb: 12,
    armor: { type: 'medium', baseAc: 12, dexCap: 2 } },
  { category: 'armor', name: 'Chain shirt',    description: 'Medium · AC 13 + Dex (max 2)', weightLb: 20,
    armor: { type: 'medium', baseAc: 13, dexCap: 2 } },
  { category: 'armor', name: 'Scale mail',     description: 'Medium · AC 14 + Dex (max 2) · disadvantage on Stealth', weightLb: 45,
    armor: { type: 'medium', baseAc: 14, dexCap: 2, stealthDis: true } },
  { category: 'armor', name: 'Breastplate',    description: 'Medium · AC 14 + Dex (max 2)', weightLb: 20,
    armor: { type: 'medium', baseAc: 14, dexCap: 2 } },
  { category: 'armor', name: 'Half plate',     description: 'Medium · AC 15 + Dex (max 2) · disadvantage on Stealth', weightLb: 40,
    armor: { type: 'medium', baseAc: 15, dexCap: 2, stealthDis: true } },
  { category: 'armor', name: 'Chain mail',     description: 'Heavy · AC 16 · Str 13 · disadvantage on Stealth', weightLb: 55,
    armor: { type: 'heavy', baseAc: 16, dexCap: 0, stealthDis: true, strMin: 13 } },
  { category: 'armor', name: 'Plate',          description: 'Heavy · AC 18 · Str 15 · disadvantage on Stealth', weightLb: 65,
    armor: { type: 'heavy', baseAc: 18, dexCap: 0, stealthDis: true, strMin: 15 } },
  { category: 'armor', name: 'Shield',         description: '+2 AC · one hand', weightLb: 6,
    armor: { type: 'shield', acBonus: 2 } },

  // ── Consumables ─────────────────────────────────────────────────────────────
  { category: 'consumable', name: 'Healing potion',         description: 'Regain 2d4 + 2 HP (action)', weightLb: 0.5,
    use: { type: 'heal', dice: '2d4', bonus: 2 } },
  { category: 'consumable', name: 'Greater healing potion', description: 'Regain 4d4 + 4 HP (action)', weightLb: 0.5,
    use: { type: 'heal', dice: '4d4', bonus: 4 } },
  { category: 'consumable', name: 'Antitoxin',              description: 'Advantage on saves vs poison for 1 hour', weightLb: 0,
    use: { type: 'buff', buffId: 'antitoxin', duration: 60 } },
  { category: 'consumable', name: 'Holy water',             description: '2d6 radiant vs fiend/undead within 5ft (thrown)', weightLb: 1,
    use: { type: null } },
  { category: 'consumable', name: 'Rations (1 day)',        description: 'Dry food, lasts a day', weightLb: 2,
    use: { type: null } },
  { category: 'consumable', name: "Alchemist's fire",       description: '1d4 fire/turn · DC 10 Dex to extinguish (thrown)', weightLb: 1,
    use: { type: null } },
  { category: 'consumable', name: 'Oil flask',              description: '5ft splash · ignites for 2 rounds', weightLb: 1,
    use: { type: null } },
  { category: 'consumable', name: 'Acid (vial)',            description: '2d6 acid damage (thrown)', weightLb: 1,
    use: { type: null } },
  { category: 'consumable', name: 'Spell scroll',           description: 'Cast a single spell (set level when used)', weightLb: 0,
    use: { type: null } },
  { category: 'consumable', name: "Healer's kit",           description: '10 charges · stabilise dying creature without a check', weightLb: 3,
    use: { type: null } },

  // ── Gear ────────────────────────────────────────────────────────────────────
  { category: 'gear', name: 'Backpack',           description: 'Holds 1 cu. ft. / 30 lb', weightLb: 5 },
  { category: 'gear', name: 'Bedroll',            description: 'Sleep on rough ground without exhaustion', weightLb: 7 },
  { category: 'gear', name: 'Mess kit',           description: 'Tin box with cutlery and a cup', weightLb: 1 },
  { category: 'gear', name: 'Tinderbox',          description: 'Light a torch (action) or a fire (1 minute)', weightLb: 1 },
  { category: 'gear', name: 'Torch',              description: 'Bright light 20ft, dim 20ft more, 1 hour', weightLb: 1 },
  { category: 'gear', name: 'Candle',             description: 'Dim light 5ft for 1 hour', weightLb: 0 },
  { category: 'gear', name: 'Hooded lantern',     description: 'Bright light 30ft, dim 30ft more, 6h on a flask of oil', weightLb: 2 },
  { category: 'gear', name: 'Bullseye lantern',   description: 'Bright light 60ft cone, 6h on a flask of oil', weightLb: 2 },
  { category: 'gear', name: 'Rope (50ft, hempen)',description: '2 HP · DC 17 Str to break', weightLb: 10 },
  { category: 'gear', name: 'Rope (50ft, silk)',  description: '2 HP · DC 17 Str to break · lighter than hempen', weightLb: 5 },
  { category: 'gear', name: 'Grappling hook',     description: 'Climb without a check on a successful throw', weightLb: 4 },
  { category: 'gear', name: 'Piton',              description: 'Iron spike for anchoring rope', weightLb: 0.25 },
  { category: 'gear', name: "Climber's kit",      description: 'Anchor in place; can\'t be moved more than 25ft while in use', weightLb: 12 },
  { category: 'gear', name: 'Hammer',             description: 'Drive pitons, break things', weightLb: 3 },
  { category: 'gear', name: 'Crowbar',            description: 'Advantage on Strength checks where leverage helps', weightLb: 5 },
  { category: 'gear', name: 'Shovel',             description: 'Dig 5 ft per 10 minutes', weightLb: 5 },
  { category: 'gear', name: 'Pot, iron',          description: 'Hold soup and stew', weightLb: 10 },
  { category: 'gear', name: 'Waterskin',          description: '4 pints of liquid', weightLb: 5 },
  { category: 'gear', name: 'Tent, two-person',   description: 'A portable canvas shelter', weightLb: 20 },
  { category: 'gear', name: 'Lock',               description: 'Key included · DC 15 Thieves\' tools to pick', weightLb: 1 },
  { category: 'gear', name: 'Manacles',           description: 'DC 20 Dex with thieves\' tools to escape', weightLb: 6 },
  { category: 'gear', name: 'Spyglass',           description: 'Magnify objects to 2× size at distance', weightLb: 1 },
  { category: 'gear', name: 'Magnifying glass',   description: 'Used in fine work; can also start fires', weightLb: 0 },
  { category: 'gear', name: "Clothes, traveler's",description: 'Practical outfit for long journeys', weightLb: 4 },
  { category: 'gear', name: 'Clothes, common',    description: 'Plain, well-worn attire', weightLb: 3 },
  { category: 'gear', name: 'Whetstone',          description: 'Maintain a weapon\'s edge', weightLb: 1 },
  { category: 'gear', name: 'Bell',               description: 'Simple alarm', weightLb: 0 },
  { category: 'gear', name: 'Hourglass',          description: 'Measures 1 hour', weightLb: 1 },
  { category: 'gear', name: 'Mirror, steel',      description: 'Polished steel reflector', weightLb: 0.5 },
  { category: 'gear', name: 'Sack',               description: 'Holds 30 lb · canvas', weightLb: 0.5 },
  { category: 'gear', name: 'Pouch',              description: 'Holds 6 lb · belt-mounted', weightLb: 1 },
  { category: 'gear', name: 'Chest',              description: 'Holds 300 lb · heavy wooden box', weightLb: 25 },
  { category: 'gear', name: "Thieves' tools",     description: 'Required for picking locks / disarming traps', weightLb: 1 },
  { category: 'gear', name: 'Disguise kit',       description: 'Used to make Deception or Performance checks for impersonation', weightLb: 3 },
  { category: 'gear', name: "Navigator's tools",  description: 'Used for navigation checks', weightLb: 2 },
  { category: 'gear', name: 'Holy symbol',        description: 'Spellcasting focus for clerics / paladins', weightLb: 1 },
  { category: 'gear', name: 'Quiver',             description: 'Holds 20 arrows', weightLb: 1 },
  { category: 'gear', name: 'Arrows ×20',         description: 'Ammunition for shortbow / longbow', weightLb: 1 },
  { category: 'gear', name: 'Bolts ×20',          description: 'Ammunition for crossbows', weightLb: 1.5 },
  { category: 'gear', name: 'Crystal (arcane focus)',          description: 'Arcane spellcasting focus', weightLb: 1 },
  { category: 'gear', name: 'Orb (arcane focus)',              description: 'Arcane spellcasting focus', weightLb: 3 },
  { category: 'gear', name: 'Rod (arcane focus)',              description: 'Arcane spellcasting focus', weightLb: 2 },
  { category: 'gear', name: 'Wand (arcane focus)',             description: 'Arcane spellcasting focus', weightLb: 1 },
  { category: 'gear', name: 'Sprig of mistletoe (druidic)',    description: 'Druidic spellcasting focus', weightLb: 0 },
  { category: 'gear', name: 'Component pouch',    description: 'Spell components storage', weightLb: 2 },
  { category: 'gear', name: 'Spellbook',          description: 'Holds wizard spells', weightLb: 3 },
  { category: 'gear', name: 'Ink (1 oz bottle)',  description: 'Calligraphy ink', weightLb: 0 },
  { category: 'gear', name: 'Vial',               description: 'Empty glass vial', weightLb: 0 },
  { category: 'gear', name: "Smith's tools",      description: 'Artisan\'s tools — smithing', weightLb: 8 },
  { category: 'gear', name: "Carpenter's tools",  description: 'Artisan\'s tools — carpentry', weightLb: 6 },
  { category: 'gear', name: "Mason's tools",      description: 'Artisan\'s tools — masonry', weightLb: 8 },
  { category: 'gear', name: "Alchemist's supplies", description: 'Artisan\'s tools — alchemy', weightLb: 8 },
  { category: 'gear', name: 'Herbalism kit',      description: 'Required to craft healing potions and antitoxin', weightLb: 3 },
  { category: 'gear', name: "Poisoner's kit",     description: 'Used to craft and apply poisons', weightLb: 2 },

  // ── Treasure templates ──────────────────────────────────────────────────────
  { category: 'treasure', name: 'Gem (10 gp)',  description: 'Azurite, banded agate, malachite, etc.', weightLb: 0 },
  { category: 'treasure', name: 'Gem (50 gp)',  description: 'Bloodstone, carnelian, citrine, jasper, etc.', weightLb: 0 },
  { category: 'treasure', name: 'Gem (100 gp)', description: 'Amber, amethyst, jade, pearl, etc.', weightLb: 0 },
  { category: 'treasure', name: 'Art object (25 gp)', description: 'Silver ewer, carved bone statuette, etc.', weightLb: 1 },
  { category: 'treasure', name: 'Trinket',      description: 'A curious oddity (DMG trinkets table)', weightLb: 0 },

  // ── Tower of Mirrored Minds relics (DM-granted one-shot rewards) ───────────
  // `relic: true` keeps these grouped at the top of the DM grant picker and
  // out of any future auto-generated shop lists.
  { category: 'armor', name: 'The Unbroken Line', relic: true, weightLb: 6,
    description: '+1 shield (+3 AC) · regiment colors; when raised, a faint sound of soldiers saluting',
    armor: { type: 'shield', acBonus: 3 } },
  { category: 'armor', name: 'Seaglass Song', relic: true, weightLb: 13,
    description: '+1 studded leather (AC 13 + Dex) · sea-glass scales; the wearer always knows the state of the tide',
    armor: { type: 'light', baseAc: 13 } },
  { category: 'weapon', name: 'Fragment of the Veil', relic: true, weightLb: 3,
    description: '+1 pact blade · 1d8 slashing (CHA) · versatile (1d10) · wrapped in true veil-cloth, always cold',
    weapon: { tier: 'martial', damageDie: 'd8', damageType: 'slashing', properties: ['versatile'], versatileDie: 'd10', stat: 'cha', magicBonus: 1 } },
  { category: 'weapon', name: 'The Finest Vintage', relic: true, weightLb: 7,
    description: '+1 greataxe · 1d12 slashing · heavy, two-handed · barrel-stave haft; pours one true pint per long rest',
    weapon: { tier: 'martial', damageDie: 'd12', damageType: 'slashing', properties: ['heavy','two-handed'], stat: 'str', magicBonus: 1 } },
  { category: 'weapon', name: 'Two Hundred Timbers', relic: true, weightLb: 1,
    description: '+1 dagger · 1d4 piercing · finesse, light, thrown · Kamatlan fang on a bone-tool haft; advantage on climbing checks (DM ruling)',
    weapon: { tier: 'simple', damageDie: 'd4', damageType: 'piercing', properties: ['finesse','light','thrown'], stat: 'dex', magicBonus: 1 } },
];

export function catalogFor(category) {
  return INVENTORY_CATALOG.filter((it) => it.category === category);
}

// Look up a catalog row by name (exact match). Used for weight fallback +
// shop description merge.
export function catalogLookup(name) {
  if (!name) return null;
  return INVENTORY_CATALOG.find((it) => it.name === name) || null;
}

// ── Encumbrance helpers (5e tiered) ──────────────────────────────────────────
// Carry limit = STR × 15 lb. Encumbered > STR × 5 (−10 ft). Heavily > STR × 10
// (−20 ft + disadvantage on STR/DEX/CON). Overburdened > STR × 15 (can't pick
// up more — shop blocks).

// 5e size category multipliers on the STR×15 base.
const SIZE_CAPACITY_MULT = {
  tiny:       0.5,
  small:      1,
  medium:     1,
  large:      2,
  huge:       4,
  gargantuan: 8,
};

export function carryCapacity(strScore, sizeCategory = 'medium') {
  const mult = SIZE_CAPACITY_MULT[sizeCategory] ?? 1;
  return (strScore ?? 10) * 15 * mult;
}

export function encumbranceThresholds(strScore, sizeCategory = 'medium') {
  const mult = SIZE_CAPACITY_MULT[sizeCategory] ?? 1;
  const base = (strScore ?? 10);
  return {
    encumbered: base * 5  * mult,
    heavily:    base * 10 * mult,
    overburdened: base * 15 * mult,
  };
}

export function currentWeight(inventory) {
  if (!inventory) return 0;
  return inventory.reduce((sum, it) => {
    const w = (it.weightLb != null)
      ? it.weightLb
      : (catalogLookup(it.name)?.weightLb ?? 0);
    return sum + w * (it.qty || 1);
  }, 0);
}

export function encumbranceTier(weightLb, strScore, sizeCategory = 'medium') {
  const t = encumbranceThresholds(strScore, sizeCategory);
  if (weightLb > t.overburdened) return 'overburdened';
  if (weightLb > t.heavily)      return 'heavily';
  if (weightLb > t.encumbered)   return 'encumbered';
  return 'normal';
}

export function speedPenalty(tier) {
  if (tier === 'heavily') return 20;
  if (tier === 'encumbered') return 10;
  return 0;
}

export const ENCUMBRANCE_LABELS = {
  normal:      { short: 'Normal',              color: '#4ade80' },
  encumbered:  { short: 'Encumbered −10 ft',   color: '#facc15' },
  heavily:     { short: 'Heavily Enc. −20 ft · disadv', color: '#fb923c' },
  overburdened:{ short: 'Overburdened',        color: '#ef4444' },
};

// ── Currency helpers ─────────────────────────────────────────────────────────

// Try to pay `targetCp` from the player's `available` coins, preferring the
// smallest denomination first so a 5 cp torch doesn't burn a platinum coin.
// If exact payment isn't possible, take one larger coin and report change.
// Returns { coinsToSpend, changeCp } on success, or null if the player can't
// afford the item even with overpayment.
export function cpToCoins(targetCp, available) {
  const work = { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };
  let owed = targetCp;
  const ascending = [...CURRENCY_DENOMS].reverse(); // cp first, pp last.

  // Pass 1 — pay exact-or-under using smallest available coins.
  for (const d of ascending) {
    if (owed <= 0) break;
    const have = available?.[d.id] || 0;
    if (have <= 0) continue;
    const useable = Math.floor(owed / d.cpValue);
    const take    = Math.min(have, useable);
    if (take > 0) {
      work[d.id] = take;
      owed -= take * d.cpValue;
    }
  }

  // Pass 2 — if still owed, take ONE coin from the smallest available
  // denomination that can cover the remainder (i.e. overpay).
  if (owed > 0) {
    for (const d of ascending) {
      if (d.cpValue < owed) continue;
      const have = (available?.[d.id] || 0) - (work[d.id] || 0);
      if (have <= 0) continue;
      work[d.id] = (work[d.id] || 0) + 1;
      owed -= d.cpValue;
      break;
    }
  }

  if (owed > 0) return null; // can't afford
  return { coinsToSpend: work, changeCp: -owed };
}

// Turn a raw cp amount into a clean coin object using the largest
// denominations first. Used for change-back so a 1 sp overpayment doesn't
// come back as 5 cp + 5 cp + ... but as 1 sp directly when it fits.
export function cpToCleanCoins(cp) {
  const out = { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 };
  let remaining = cp;
  for (const d of CURRENCY_DENOMS) {
    out[d.id] = Math.floor(remaining / d.cpValue);
    remaining -= out[d.id] * d.cpValue;
  }
  return out;
}

// Subtract coinsToSpend from the player's currency, then add the cleaned-up
// change. Returns a new currency object.
export function applyCoinTransaction(currency, coinsToSpend, changeCp) {
  const out = { ...currency };
  for (const d of CURRENCY_DENOMS) {
    out[d.id] = (out[d.id] || 0) - (coinsToSpend[d.id] || 0);
  }
  if (changeCp > 0) {
    const change = cpToCleanCoins(changeCp);
    for (const d of CURRENCY_DENOMS) {
      out[d.id] = (out[d.id] || 0) + (change[d.id] || 0);
    }
  }
  return out;
}

// Format a copper amount for display, choosing the highest sensible
// denomination. Used by ShopPanel prices.
export function formatPriceCp(cp) {
  if (cp >= 100 && cp % 100 === 0) return `${cp / 100} gp`;
  if (cp >= 100)                   return `${(cp / 100).toFixed(2)} gp`;
  if (cp >= 10 && cp % 10 === 0)   return `${cp / 10} sp`;
  return `${cp} cp`;
}

// ── Strict-match stacking (used by shop purchases to avoid clobbering
// same-named custom items) ───────────────────────────────────────────────────

export function addItemStrict(inventory, item) {
  const list = [...(inventory || [])];
  const match = list.findIndex((row) =>
    row.name        === item.name &&
    row.description === item.description &&
    row.category    === item.category
  );
  if (match >= 0) {
    list[match] = { ...list[match], qty: (list[match].qty || 1) + (item.qty || 1) };
  } else {
    list.push({
      id:        (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      category:  item.category,
      name:      item.name,
      description: item.description || '',
      qty:       item.qty || 1,
      weightLb:  item.weightLb ?? 0,
      equipped:  false,
      ...item._extra, // any structured fields (weapon/armor/use)
    });
  }
  return list;
}
